export default class ObjectChecker {
  static getFirstNotEmptyObj(...args: any[]) {
    for (let arg of args)
      if (this.isNotEmptyOrNull(arg))
        return arg;
    return this.getFirstNotNullObj(...args);
  }

  static isNotEmptyOrNull<T extends any>(o: (T | null | undefined)): o is T {
    return !this.isEmptyOrNull(o);
  }

  static isEmptyOrNull(o: any): o is (null | undefined | [] | '') {
    if (!o)
      return true;
    if (o === "null")
      return true;
    if (o === "undefined")
      return true;
    if (this.isEmptyObject(o))
      return true;
    return o.length !== undefined && o.length === 0;
  }

  static isDeepEmpty(value: any, treatZeroAsEmpty?: boolean): boolean {
    const stack: any[] = [value];
    while (stack.length > 0) {
      const current = stack.pop();
      if (current == null)
        continue;
      const type = typeof current;
      if (type === 'string') {
        if (current.length > 0 && current.trim().length > 0)
          return false;
        continue;
      }
      if (type === 'number') {
        if (!treatZeroAsEmpty || current !== 0)
          return false;
        continue;
      }
      if (type === 'boolean' || current instanceof Date)
        return false;
      if (Array.isArray(current)) {
        for (let i = 0; i < current.length; i++)
          stack.push(current[i]);
        continue;
      }
      if (type === 'object') {
        for (const key in current) {
          if (Object.prototype.hasOwnProperty.call(current, key))
            stack.push(current[key]);
        }
        continue;
      }
      return false;
    }
    return true;
  }


  static getFirstNotNullObj(...args: any[]) {
    for (let arg of args)
      if (!(this.isNullOrUndefined(arg)))
        return arg;
    return null;
  }

  static isNullOrUndefined<T>(o: T | null | undefined): o is (null | undefined) {
    if (o === undefined)
      return true;
    return o === null;
  }

  static isNotNullOrUndefined(o: any): boolean {
    return !this.isNullOrUndefined(o);
  }

  static areEqual(o1: any, o2: any) {
    if (o1 === o2)
      return true;
    if (o1 && o2) {
      if (o1.id || o2.id)
        return o1.id === o2.id;
      if (o1.empty && o2.empty)
        return true;
    }
    return JSON.stringify(o1) === JSON.stringify(o2);
  }

  static areAllEqual(...array: any[]): boolean {
    if (array.length <= 1)
      return true;
    else
      return array.every(el => this.areEqual(el, array[0]))
  }

  static areNotEqual(o1: any, o2: any) {
    return !this.areEqual(o1, o2);
  }

  static isAnyEqualToFirst(first: any, ...args: any[]) {
    for (let arg of args)
      if (this.areEqual(first, arg))
        return true;
    return false;
  }

  static valueOfBoolean(value: string|undefined) {
    if (this.isEmptyOrNull(value))
      return null;
    return value?.toLowerCase().trim() === 'true';
  }

  static areAllEmptyOrNull(...args: any[]) {
    for (let arg of args) {
      if (this.isNotEmptyOrNull(arg))
        return false;
    }
    return true;
  }

  static isAnyTrue(...args: any[]): boolean {
    for (let arg of args)
      if (arg === true)
        return true;
    return false;
  }

  static isAnyEmptyOrNull(...args: any[]) {
    for (let arg of args) {
      if (this.isEmptyOrNull(arg))
        return true;
    }
    return false;
  }

  static isAnyNotEmptyOrNull(...args: any[]) {
    for (let arg of args) {
      if (this.isNotEmptyOrNull(arg))
        return true;
    }
    return false;
  }

  static findIndexOf<T>(arr: T[], predicate: (value: T, index: number, arr: T[]) => unknown): number | undefined {
    for (let i = 0; i < arr.length; i++) {
      if (predicate(arr[i], i, arr))
        return i;
    }
  }

  static isObjectAndNotEmpty(o?: any) {
    return this.isObject(o) && !this.isEmptyObject(o);
  }

  static isEmptyObject(o?: any) {
    return o && this.isObject(o) && Object.keys(o).length === 0
  }

  static isObject(value: any): value is Record<string, any> {
    return value !== null && typeof value == "object" && !this.isArray(value);
  }

  static isNotObject(value: any): boolean {
    return !this.isObject(value);
  }

  static isString(value: any): value is string {
    return typeof value == "string";
  }

  static isArray(value: unknown): value is any[] {
    return Array.isArray(value)
  }

  static toStringOrEmpty(value: string | null | undefined): string {
    if (this.isNullOrUndefined(value))
      return "";
    else
      return value;
  }

  static isBoolean(value:unknown):value is boolean {
    return typeof value === "boolean";
  }

  static isNumber(value: unknown) :value is number{
    return typeof  value === "number" && !isNaN(value);
  }

  static areDeepEqual(a: any, b: any): boolean {
    if (a === b)
      return true;
    if (!a || !b)
      return a === b;
    if (typeof a !== typeof b)
      return false;
    if (a instanceof Date && b instanceof Date)
      return a.getTime() === b.getTime();
    if (this.isArray(a)) {
      if (!this.isArray(b) || a.length !== b.length)
        return false;
      for (let i = 0; i < a.length; i++) {
        if (!this.areDeepEqual(a[i], b[i]))
          return false;
      }
      return true;
    }
    if (this.isObject(a)) {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);
      if (keysA.length !== keysB.length)
        return false;
      for (const key of keysA) {
        if (!Object.prototype.hasOwnProperty.call(b, key))
          return false;
        if (!this.areDeepEqual(a[key], b[key]))
          return false;
      }
      return true;
    }
    return false;
  }


  static areNotDeepEqual(a: any, b: any): boolean {
    return !this.areDeepEqual(a, b);
  }
}
